/**
 * Building Renderer - renders buildings with faction colors
 * Uses modular building mesh creators for maintainability
 */

import * as THREE from 'three';
import type { FactionId, BuildingType, Entity } from '@/types';
import { FACTIONS } from '@/game/factions/FactionData';
import {
  createBarracksMesh,
  createCapitalMesh,
  createTowerMesh,
  createMineMesh,
  createFarmMesh,
  createMarketMesh,
  createStableMesh,
  createSmithyMesh,
  createTempleMesh,
  createArcheryMesh,
  createSpearcampMesh,
  createWallMesh,
} from './meshes';
import type { WallVariant } from '@/game/components/Wall';

interface BuildingRenderData {
  entity: Entity;
  mesh: THREE.Group;
  buildingType: BuildingType;
  factionId: FactionId;
  flagMesh?: THREE.Mesh;
  wallVariant?: WallVariant;
  wallRotation?: number;
}

export class BuildingRenderer {
  private scene: THREE.Scene;
  private buildings: Map<Entity, BuildingRenderData> = new Map();
  private time: number = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  private createBuildingMesh(buildingType: BuildingType, factionId: FactionId): THREE.Group {
    const group = new THREE.Group();
    const faction = FACTIONS[factionId];
    const mainColor = new THREE.Color(faction.color);
    const accentColor = new THREE.Color(faction.secondaryColor);

    switch (buildingType) {
      case 'capital':
        createCapitalMesh(group, mainColor, accentColor);
        break;
      case 'barracks':
        createBarracksMesh(group, mainColor, accentColor);
        break;
      case 'tower':
        createTowerMesh(group, mainColor, accentColor);
        break;
      case 'mine':
        createMineMesh(group, mainColor);
        break;
      case 'farm':
        createFarmMesh(group, mainColor);
        break;
      case 'market':
        createMarketMesh(group, mainColor, accentColor);
        break;
      case 'stable':
        createStableMesh(group, mainColor, accentColor);
        break;
      case 'smithy':
        createSmithyMesh(group, mainColor, accentColor);
        break;
      case 'temple':
        createTempleMesh(group, mainColor, accentColor);
        break;
      case 'archery':
        createArcheryMesh(group, mainColor, accentColor);
        break;
      case 'spearcamp':
        createSpearcampMesh(group, mainColor, accentColor);
        break;
      case 'wall':
        // Wall uses default 'single' variant initially
        createWallMesh(group, mainColor, accentColor, 'single', 0);
        break;
      default:
        createBarracksMesh(group, mainColor, accentColor);
    }

    return group;
  }

  /**
   * Create a wall mesh with specific variant and rotation
   */
  private createWallMeshWithVariant(
    factionId: FactionId,
    variant: WallVariant,
    rotation: number
  ): THREE.Group {
    const group = new THREE.Group();
    const faction = FACTIONS[factionId];
    const mainColor = new THREE.Color(faction.color);
    const accentColor = new THREE.Color(faction.secondaryColor);
    createWallMesh(group, mainColor, accentColor, variant, rotation);
    return group;
  }

  addBuilding(
    entity: Entity,
    buildingType: BuildingType,
    x: number,
    y: number,
    z: number,
    factionId: FactionId,
    wallVariant?: WallVariant,
    wallRotation?: number
  ): void {
    if (this.buildings.has(entity)) return;

    let mesh: THREE.Group;
    if (buildingType === 'wall' && wallVariant !== undefined) {
      mesh = this.createWallMeshWithVariant(factionId, wallVariant, wallRotation ?? 0);
    } else {
      mesh = this.createBuildingMesh(buildingType, factionId);
    }

    // Add offset to place building on top of terrain surface
    // Use smaller offset for walls since they sit closer to ground
    const surfaceOffset = buildingType === 'wall' ? 0.0 : 0.5;
    mesh.position.set(x, y + surfaceOffset, z);
    this.scene.add(mesh);

    // Find flag mesh if exists
    let flagMesh: THREE.Mesh | undefined;
    mesh.traverse((child) => {
      if (child instanceof THREE.Mesh && child.name === 'flag') {
        flagMesh = child;
      }
    });

    this.buildings.set(entity, {
      entity,
      mesh,
      buildingType,
      factionId,
      flagMesh,
      wallVariant: buildingType === 'wall' ? (wallVariant ?? 'single') : undefined,
      wallRotation: buildingType === 'wall' ? (wallRotation ?? 0) : undefined,
    });
  }

  /**
   * Update wall variant and rotation (called when connections change)
   */
  updateWallVariant(entity: Entity, variant: WallVariant, rotation: number): void {
    const data = this.buildings.get(entity);
    if (!data || data.buildingType !== 'wall') return;

    // Check if variant or rotation changed
    if (data.wallVariant === variant && data.wallRotation === rotation) {
      return;
    }

    // Store current position
    const position = data.mesh.position.clone();

    // Remove old mesh from scene and dispose
    this.scene.remove(data.mesh);
    data.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });

    // Create new mesh with updated variant
    const newMesh = this.createWallMeshWithVariant(data.factionId, variant, rotation);
    newMesh.position.copy(position);
    this.scene.add(newMesh);

    // Update tracking data
    data.mesh = newMesh;
    data.wallVariant = variant;
    data.wallRotation = rotation;
  }

  removeBuilding(entity: Entity): void {
    const data = this.buildings.get(entity);
    if (!data) return;

    this.scene.remove(data.mesh);
    // Dispose geometries and materials
    data.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });

    this.buildings.delete(entity);
  }

  updateBuilding(entity: Entity, x: number, y: number, z: number): void {
    const data = this.buildings.get(entity);
    if (data) {
      const surfaceOffset = 0.5;
      data.mesh.position.set(x, y + surfaceOffset, z);
    }
  }

  /**
   * Update animations (flag waving)
   */
  update(deltaTime: number): void {
    this.time += deltaTime;

    // Animate flags
    for (const data of this.buildings.values()) {
      if (data.flagMesh) {
        // Simple wave animation
        data.flagMesh.rotation.y = Math.sin(this.time * 2 + data.entity * 0.5) * 0.2;
        data.flagMesh.position.x = 0.35 + Math.sin(this.time * 3) * 0.02;
      }
    }
  }

  getBuildingCount(): number {
    return this.buildings.size;
  }

  clear(): void {
    for (const entity of this.buildings.keys()) {
      this.removeBuilding(entity);
    }
  }

  dispose(): void {
    this.clear();
  }
}
